import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { updateBook } from '../../api/books';
import api from '../../api/client';
import { useQuery as useQ } from '@tanstack/react-query';
import Modal from '../ui/Modal';
import FormField, { Input, Select, Textarea, FormRow, SubmitBtn } from '../ui/FormField';
import toast from 'react-hot-toast';

export default function EditBookModal({ open, onClose, book }: { open: boolean; onClose: () => void; book: any }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<any>({});
  const [loading, setLoading] = useState(false);

  const { data: accounts } = useQ({
    queryKey: ['accounts'],
    queryFn: () => api.get('/acquisitions/accounts'),
    enabled: open
  } as any);

  const { data: colls } = useQuery({
    queryKey: ['collections'],
    queryFn: () => api.get('/books/collections'),
    enabled: open
  } as any);

  useEffect(() => {
    if (book) setForm({
      title: book.title || '',
      author: book.author || '',
      isbn: book.isbn || '',
      collection_id: book.collection_id || '',
      publisher: book.publisher || '',
      pub_year: book.pub_year || '',
      language: book.language || 'ka',
      description: book.description || '',
      cover_url: book.cover_url || '',
      account_code: book.account_code || '',
      sub_account: book.sub_account || '',
      full_account: book.full_account || '',
      acquisition_price: book.acquisition_price || '',
      editor: book.editor || '',
      translator: book.translator || '',
      place_of_publication: book.place_of_publication || '',
      material_type: book.material_type || 'book',
      subject: book.subject || '',
      series: book.series || '',
      series_number: book.series_number || '',
      volume: book.volume || '',
      volume_number: book.volume_number || '',
      udc: book.udc || '',
      shelf_code: book.shelf_code || '',
      inventory_number: book.inventory_number || '',
      notes: book.notes || '',
    });
  }, [book]);

  const set = (k: string) => (e: any) => setForm((f: any) => ({...f, [k]: e.target.value}));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title) return toast.error('სათაური სავალდებულოა');
    setLoading(true);
    try {
      await updateBook(book.id, form);
      toast.success('წიგნი განახლდა!');
      qc.invalidateQueries({ queryKey: ['book', String(book.id)] });
      qc.invalidateQueries({ queryKey: ['books'] });
      onClose();
    } catch { toast.error('შეცდომა'); }
    finally { setLoading(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="წიგნის რედაქტირება" width={540}>
      <form onSubmit={handleSubmit}>
        <FormField label="სათაური" required>
          <Input value={form.title||''} onChange={set('title')} placeholder="წიგნის სათაური" />
        </FormField>
        <FormField label="ავტორი">
          <Input value={form.author||''} onChange={set('author')} placeholder="ავტორი" />
        </FormField>
        <FormRow>
          <FormField label="რედაქტორი">
            <Input value={form.editor||''} onChange={set('editor')} placeholder="რედაქტორი" />
          </FormField>
          <FormField label="მთარგმნელი">
            <Input value={form.translator||''} onChange={set('translator')} placeholder="მთარგმნელი" />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="ISBN">
            <Input value={form.isbn||''} onChange={set('isbn')} placeholder="978-..." />
          </FormField>
          <FormField label="ენა">
            <Select value={form.language||'ka'} onChange={set('language')}>
              <option value="ka">ქართული</option>
              <option value="en">English</option>
              <option value="ru">Русский</option>
              <option value="other">სხვა</option>
            </Select>
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="გამომცემელი">
            <Input value={form.publisher||''} onChange={set('publisher')} placeholder="გამომცემლობა" />
          </FormField>
          <FormField label="გამოცემის ადგილი">
            <Input value={form.place_of_publication||''} onChange={set('place_of_publication')} placeholder="თბილისი, Moscow..." />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="გამოცემის წელი">
            <Input value={form.pub_year||''} onChange={set('pub_year')} placeholder="2024" />
          </FormField>
          <FormField label="გამოცემა">
            <Input value={form.edition||''} onChange={set('edition')} placeholder="მე-2 გამოცემა..." />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="მასალის ტიპი">
            <Select value={form.material_type||'book'} onChange={set('material_type')}>
              <option value="book">წიგნი</option>
              <option value="periodical">პერიოდიკა</option>
              <option value="dissertation">დისერტაცია</option>
              <option value="dissertation_abstract">სადისერტაციო მაცნე</option>
              <option value="cd">CD</option>
              <option value="proceedings">შრომები</option>
              <option value="xerox">ქსეროასლი</option>
            </Select>
          </FormField>
          <FormField label="კოლექცია">
            <Select value={form.collection_id||''} onChange={set('collection_id')}>
              <option value="">— კოლექცია —</option>
              {((colls as any)?.data?.collections||[]).map((c: any) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="სრული საბუღ. ანგარიში *" required>
            <Input value={form.full_account||''} onChange={set('full_account')} placeholder="2157 1 05922" />
          </FormField>
          <FormField label="ქვეანგარიში">
            <Input value={form.sub_account||''} onChange={set('sub_account')} placeholder="1" />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="საბუღ. კოდი">
            <Input value={form.account_code||''} onChange={set('account_code')} placeholder="05922" />
          </FormField>
          <FormField label="ასლის ფასი (₾)">
            <Input type="number" min="0" step="0.01" value={form.acquisition_price||''} onChange={set('acquisition_price')} placeholder="0.00" />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="საგანი/თემა">
            <Input value={form.subject||''} onChange={set('subject')} placeholder="ისტორია, ფიზიკა..." />
          </FormField>
          <FormField label="UDC კლასიფიკაცია">
            <Input value={form.udc||''} onChange={set('udc')} placeholder="94(479.22)..." />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="სერია">
            <Input value={form.series||''} onChange={set('series')} placeholder="სერიის სახელი" />
          </FormField>
          <FormField label="სერიის ნომერი">
            <Input value={form.series_number||''} onChange={set('series_number')} placeholder="№5..." />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="ტომი">
            <Input value={form.volume||''} onChange={set('volume')} placeholder="I, II, 1..." />
          </FormField>
          <FormField label="ტომის ნომერი">
            <Input value={form.volume_number||''} onChange={set('volume_number')} placeholder="3..." />
          </FormField>
        </FormRow>
        <FormField label="გარეკანის URL">
          <Input value={form.cover_url||''} onChange={set('cover_url')} placeholder="https://..." />
        </FormField>
        <FormField label="აღწერა">
          <Textarea value={form.description||''} onChange={set('description')} placeholder="მოკლე აღწერა..." />
        </FormField>
        <FormRow>
          <FormField label="თაროს კოდი (მაგ. ბ-12)">
            <Input value={form.shelf_code||''} onChange={set('shelf_code')} placeholder="ბ-12, A-5..." />
          </FormField>
          <FormField label="საინვენტარო ნომერი">
            <Input value={form.inventory_number||''} onChange={set('inventory_number')} placeholder="INV-001..." />
          </FormField>
        </FormRow>
        <FormField label="შენიშვნა">
          <Input value={form.notes||''} onChange={set('notes')} placeholder="შიდა შენიშვნა..." />
        </FormField>
        <SubmitBtn loading={loading} label="შენახვა" />
      </form>
    </Modal>
  );
}
